import { Directive, ElementRef, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';

// Animates an element's text content counting up from its previous value
// to a new one whenever [countUp] changes — e.g. the dashboard's stat
// cards (Students/Broadcasts Sent/Staff/Classes), which previously just
// snapped straight to the final number. Accepts the already-formatted
// string these cards store (built via toLocaleString('en'), so it may
// contain commas) and re-parses/reformats it on every animation frame, so
// the comma grouping stays correct throughout the count, not just at the
// end.
@Directive({
  selector: '[countUp]',
})
export class CountUpDirective implements OnChanges, OnDestroy {
  @Input() countUp: string | number | null | undefined;
  @Input() countUpDurationMs = 800;
  // Optional currency symbol (or any prefix) rendered before the number on
  // every frame — e.g. fees-v2's stat cards ("$348,000"), where the symbol
  // itself can change at runtime (school currency), unlike a static
  // template string prefix.
  @Input() countUpPrefix = '';

  private currentValue = 0;
  private animationFrameId: number | null = null;

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!('countUp' in changes)) {
      return;
    }
    const target = this.parseValue(this.countUp);
    if (target === null) {
      return;
    }
    // First real value (previous was null/undefined, e.g. right after the
    // loading skeleton) — no previous number to animate FROM, so count up
    // from 0 rather than skipping the animation on first load.
    const startValue = changes.countUp.isFirstChange() ? 0 : this.currentValue;
    this.animateTo(startValue, target);
  }

  ngOnDestroy(): void {
    this.cancelAnimation();
  }

  private parseValue(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numeric = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    return isNaN(numeric) ? null : numeric;
  }

  private animateTo(from: number, to: number): void {
    this.cancelAnimation();
    if (from === to) {
      this.render(to);
      return;
    }
    const start = performance.now();
    const duration = this.countUpDurationMs;
    const step = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out — fast start, settles into the final value rather than a
      // linear count that feels mechanical.
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(from + (to - from) * eased);
      this.render(value);
      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(step);
      } else {
        this.currentValue = to;
      }
    };
    this.animationFrameId = requestAnimationFrame(step);
  }

  private render(value: number): void {
    this.currentValue = value;
    this.elementRef.nativeElement.textContent = this.countUpPrefix + value.toLocaleString('en');
  }

  private cancelAnimation(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }
}
